// Light or dark: the operating system's, until the person picks one here.
//
// The scheme is an attribute on <html>, so switching it re-themes the design system through CSS
// alone. A choice is kept in this browser; without one, the page follows the system, also when
// the system changes while the page is open.

export type ColorScheme = "light" | "dark";

const KEY = "sortera-chat:color-scheme";
const system = window.matchMedia("(prefers-color-scheme: dark)");
const listeners = new Set<(scheme: ColorScheme) => void>();

function chosen(): ColorScheme | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

export function currentColorScheme(): ColorScheme {
  return chosen() ?? (system.matches ? "dark" : "light");
}

function apply(): void {
  const scheme = currentColorScheme();
  document.documentElement.setAttribute("data-wk-color-scheme", scheme);
  for (const listener of listeners) listener(scheme);
}

/** Sets the scheme now, and keeps following the system for as long as nothing is chosen. */
export function startColorScheme(): void {
  apply();
  system.addEventListener("change", apply);
}

export function chooseColorScheme(scheme: ColorScheme): void {
  try {
    localStorage.setItem(KEY, scheme);
  } catch {
    // Private window or blocked storage: the choice holds until the page is closed.
    document.documentElement.setAttribute("data-wk-color-scheme", scheme);
    for (const listener of listeners) listener(scheme);
    return;
  }
  apply();
}

export function onColorScheme(listener: (scheme: ColorScheme) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
